import { action, observable, computed } from 'mobx';
import { API } from '@/config/api';
import Axios from 'axios';
import { Notification } from 'element-react';
import { PageInfo } from '@/models/data';
import { SendStatusEnum, RewardTypeEnum, TaskTypeEnum, TaskStatusEnum, PrizeTypeEnum } from '@/@enums/Activity';
import { BigNumber } from 'bignumber.js';
import i18n from '@/i18n';
import { getUserCenterOrigin } from '@/utils/location';
import { getClientCode } from '@/utils/fn';

export type ActivityType = 'INVITED' | 'PDD' | 'BONUS' | 'OTHER' | '';

export interface ActivityCondition {
  fullAmount: number; // 满额发放
  isNeedKyc: boolean; // 是否需要kyc
  isWholeProcess: boolean; // 是否为全流程
}

export interface ActivityItem {
  cashbackRewardTotal: number; // 返现金总量
  coin: string; // 币种
  condition: ActivityCondition; // 条件
  createdTime: Date; // 开始时间
  expireTime: Date; // 结束时间	string(date-time)
  experienceRewardTotal: number; // 体验金总量
  id: number;
  maxPersonalReward: number; // 个人最大奖励
  name: string; // 名称
  type: ActivityType; // 类型
  // totalTaskReward: number; // 总的任务奖励
  totalTaskCashBackReward: 0; // 返现盛夏活动
  totalTaskBonusReward: 0; // 体验金
}

export interface TaskItem {
  activityId: number;
  createdTime: Date;
  id: number;
  maxPersonalReward: number;
  name: string;
  rewardAmount: number;
  rewardRule: string;
  rewardType: RewardTypeEnum;
  type: TaskTypeEnum;
  coin: string;
  condition: Condition;
  targetCount: number;
}
export interface Condition {
  fullAmount: number;
  isNeedKyc: boolean;
  isWholeProcess: boolean;
}

export interface ActiveProgressData {
  total: number;
  limit: number;
  current: number;
}

export interface ActiveProgressCanGet {
  canGet: boolean;
}

export type RewardStatus = 'CANNOT_RECEIVE' | 'CAN_RECEIVE' | 'ALREADY_RECEIVE';

export interface UserReward {
  coin: string;
  bonus: number;
  cashback: number;
  remainBonus: number; // 未领取体验金	number(bigdecimal)
  remainCashback: number; // 未领取现金返还
  rewards: UserRewardItem[];
}

export interface UserRewardItem {
  activityId: number;
  automatic: number;
  coin: string;
  id: number;
  rewardAmount: number;
  rewardType: RewardTypeEnum;
  status: RewardStatus;
  taskId: number;
}

export interface TurntableItem {
  coinType: string; // 奖励币种
  img: string; // 奖品图片
  perPrizeNum: number; // 数量
  prizeType: PrizeTypeEnum; // 奖励类型
}

export interface LotteryItem {
  id: number; // 中奖记录ID
  num: number;
  prize: string;
  prizeId: number;
  prizeType: PrizeTypeEnum;
  sendStatus: SendStatusEnum;
  winDate: Date;
}

export interface LotteryResult {
  id: number; // 奖励ID
  img: string;
  perPrizeNum: number;
  prize: string;
  prizeType: PrizeTypeEnum;
}

export interface TurntableCfg {
  num: number;
  prize: TurntableItem[];
}

export interface TurntableTaskProgress {
  activityId: number;
  coin: string;
  finishedTradeAmount: number;
  targetTradeAmount: number;
}

export interface InitialRewardInfo {
  activityId: number;
  coin: string;
  amount: number;
  hasReceived: boolean;
}

export interface CountryInfo {
  ipCountryId: number;
  ipCountryCode: string;
  defaultCountryId: number;
  countryList: Country[];
}

export interface Country {
  id: number;
  xtId: number;
  name: string;
  des: string;
  code: string;
  shortName: string;
  nationalFlagIcon: string;
  nameEn: string;
  providerCode: string;
  providerIdType: number[];
  isKyc: number;
}

export interface CountryIpCheck {
  ip: string;
  countryId: number;
  isIntercept: boolean;
}

export interface PublicKeyInfo {
  passwdId: string;
  publicKey: string;
}

export interface RegisterResult {
  loginStatus: number;
  mfaId: string;
  userVO: UserInfo;
}

export interface UserInfo {
  lastAuthTime: number;
  source: string;
  userCode: string;
  userId: number;
  userName: string;
}

export default class Activity {
  @observable detail: ActivityItem;

  @observable taskList: TaskItem[];

  @observable userReward: UserReward;

  @observable userLotteryList: LotteryItem[];

  @observable userLotteryListPageInfo: PageInfo;

  @observable platformLotteryList: LotteryItem[];

  @observable turntableList: TurntableItem[];

  @observable turntableCount: number;

  @observable isFetchingDetail: boolean;

  @observable isFetchingTaskList: boolean;

  @observable isFetchingDrawing: boolean;

  @observable isFetchingUserLottery: boolean;

  @observable isFetchingReceive: boolean;

  @observable turntableTaskProgress: TurntableTaskProgress;

  @observable initialRewardInfo: InitialRewardInfo;

  @observable countryInfo: CountryInfo;

  @observable countryIpCheckInfo: CountryIpCheck;

  constructor() {
    this.detail = {
      totalTaskCashBackReward: 0,
      totalTaskBonusReward: 0,
      cashbackRewardTotal: 0,
      coin: '',
      condition: {
        fullAmount: 0,
        isNeedKyc: false,
        isWholeProcess: false,
      },
      createdTime: new Date(),
      expireTime: new Date(),
      experienceRewardTotal: 0,
      id: 0,
      maxPersonalReward: 0,
      name: '',
      type: '',
    };
    this.taskList = [];
    this.userReward = {
      coin: '',
      bonus: 0, // 已领取体验金
      cashback: 0, // 已领取现金返还
      remainBonus: 0, // 未领取体验金
      remainCashback: 0, // 未领取现金返还
      rewards: [],
    };

    this.userLotteryList = [];
    this.userLotteryListPageInfo = {
      page: 1,
      size: 10,
      total: 0,
    };

    this.platformLotteryList = [];
    this.turntableList = [];

    this.isFetchingDetail = false;
    this.isFetchingTaskList = false;
    this.isFetchingDrawing = false;
    this.isFetchingUserLottery = false;
    this.isFetchingReceive = false;
    this.turntableCount = 0;

    this.turntableTaskProgress = {
      activityId: 0,
      coin: '',
      finishedTradeAmount: 0,
      targetTradeAmount: 0,
    };

    this.initialRewardInfo = {
      activityId: 0,
      coin: '',
      amount: 0,
      hasReceived: true,
    };
    this.countryInfo = {} as CountryInfo;
    this.countryIpCheckInfo = {} as CountryIpCheck;
  }

  @action.bound
  setLoadingState(key: string, data: boolean) {
    // @ts-ignore
    this[key] = data;
  }

  //
  // 详情
  //
  @action.bound
  setDetail(data: ActivityItem) {
    this.detail = data;
  }

  @action.bound
  async fetchActivityDetail(id: string | number) {
    this.setLoadingState('isFetchingDetail', true);

    const result: ActivityItem = await Axios.get(API.ACTIVITY_DETAIL, {
      params: {
        activityId: id,
      },
    });

    if (result) {
      this.setDetail(result);
    }

    this.setLoadingState('isFetchingDetail', false);
  }

  //
  // 任务列表
  //
  @action.bound
  setTaskList(data: TaskItem[]) {
    this.taskList = data;
  }

  @action.bound
  async fetchTaskList(id: string | number) {
    this.setLoadingState('isFetchingTaskList', true);

    const result: TaskItem[] = await Axios.get(API.ACTIVITY_TASK_LIST, {
      params: {
        activityId: id,
      },
    });

    if (result) {
      this.setTaskList(result);
    }

    this.setLoadingState('isFetchingTaskList', false);
  }

  //
  // 奖励列表
  //
  @action.bound
  setUserReward(data: UserReward) {
    this.userReward = data;
  }

  @action.bound
  async fetchUserReward(id: string | number) {
    const result: UserReward = await Axios.get(API.ACTIVITY_REWARDS, {
      params: {
        activityId: id,
      },
    });

    if (result) {
      this.setUserReward(result);
    }
  }

  @computed get userRewardList() {
    return this.userReward.rewards || [];
  }

  //
  // 用户转盘奖励
  //
  @action.bound
  setUserLotteryList(data: LotteryItem[]) {
    this.userLotteryList = data;
  }

  @action.bound
  setUserLotteryListPageInfo(data: PageInfo) {
    const origin = { ...this.userLotteryListPageInfo };
    this.userLotteryListPageInfo = { ...origin, ...data };
  }

  @action.bound
  async fetchUserLotteryList(params: { id: number | string; page?: number; size?: number }) {
    this.setLoadingState('isFetchingUserLottery', true);

    const result: any = await Axios.get(API.ACTIVITY_LOTTERY_USER_RECORD, {
      params: {
        activityId: params.id,
        page: params.page,
        size: params.size,
      },
    });

    if (result) {
      const { items, page, ps: size, total } = result;

      if (Array.isArray(items)) {
        this.setUserLotteryList(items);

        this.setUserLotteryListPageInfo({ page, size, total });
      }
    }

    this.setLoadingState('isFetchingUserLottery', false);
  }

  //
  // 平台转盘奖励
  //
  @action.bound
  setPlatformLotteryList(data: LotteryItem[]) {
    this.platformLotteryList = data;
  }

  @action.bound
  async fetchPlatformLotteryList(id: string | number) {
    const result: LotteryItem[] = await Axios.get(API.ACTIVITY_LOTTERY_PLATFORM_RECORD, {
      params: {
        activityId: id,
      },
    });
    if (result) {
      this.setPlatformLotteryList(result);
    }
  }

  // 抽奖
  @action.bound
  async fetchDraw(id: string | number): Promise<any> {
    this.setLoadingState('isFetchingDrawing', true);
    const result = await Axios.post(API.ACTIVITY_DRAW, {
      activityId: id,
    }).catch(error => {
      this.setLoadingState('isFetchingDrawing', false);
      return Promise.reject(error);
    });

    this.setLoadingState('isFetchingDrawing', false);

    return Promise.resolve(result);
  }

  // 任务：领取奖励
  @action.bound
  async fetchReceiveReward(id: string | number, taskId?: string | number): Promise<any> {
    this.setLoadingState('isFetchingReceive', true);

    const result = await Axios.post(API.ACTIVITY_RECEIVE, {
      activityId: id,
      taskId,
    }).catch(() => {
      Notification.error({
        message: i18n.t('failure.withdrawNow'),
        offset: 60,
        duration: 2000,
      });
    });


    this.setLoadingState('isFetchingReceive', false);

    if (result) {
      Notification.success({
        message: i18n.t('success.withdrawNow'),
        offset: 60,
        duration: 2000,
      });


      return Promise.resolve();
    }
    Notification.error({
      message: i18n.t('failure.withdrawNow'),
      offset: 60,
      duration: 2000,
    });


    return Promise.reject();
  }

  // 抽奖：领取奖励
  @action.bound
  async fetchReceiveLottery(id: string | number): Promise<any> {
    this.setLoadingState('isFetchingReceive', true);

    const result = await Axios.post(API.ACTIVITY_RECEIVE_LOTTERY, {
      id,
    }).catch(() => {
      Notification.error({
        message: i18n.t('failure.withdrawNow'),
        offset: 60,
        duration: 2000,
      });
    });


    this.setLoadingState('isFetchingReceive', false);

    if (result) {
      Notification.success({
        message: i18n.t('success.withdrawNow'),
        offset: 60,
        duration: 2000,
      });


      return Promise.resolve();
    }
    Notification.error({
      message: i18n.t('failure.withdrawNow'),
      offset: 60,
      duration: 2000,
    });


    return Promise.reject();
  }

  // 活动邀请访问统计
  @action.bound
  async fetchShareLinkVisit(activityId: string | number, recommendCode: string | number): Promise<any> {
    const result = await Axios.post(API.ACTIVITY_SHARE_LINK, {
      activityId,
      recommendCode,
    });

    if (result) {
      return Promise.resolve(result);
    }
    return Promise.reject();
  }

  // 返现金进度信息
  @computed get cashbackProgressData(): ActiveProgressData {
    const fullAmount = new BigNumber(this.detail?.condition?.fullAmount).toNumber();
    const cashback = new BigNumber(this.userReward.cashback).plus(new BigNumber(this.userReward.remainCashback));

    return {
      total: new BigNumber(this.detail?.totalTaskCashBackReward).toNumber(),
      limit: new BigNumber(fullAmount).toNumber(),
      current: new BigNumber(cashback).toNumber(),
    };
  }

  // 返现金进度信息
  @computed get canGetSummerReward(): boolean {
    // eslint-disable-next-line  no-restricted-syntax
    for (const key in this.userRewardList) {
      if (Object.prototype.hasOwnProperty.call(this.userRewardList, key)) {
        const element = this.userRewardList[key];
        if (element.status === TaskStatusEnum.CAN_RECEIVE) {
          return true;
        }
      }
    }
    return false;
  }

  // 体验金进度信息
  @computed get bonusProgressData(): ActiveProgressData {
    const fullAmount = this.detail?.condition?.fullAmount;
    const bonus = this.userReward.bonus;

    return {
      total: new BigNumber(this.detail?.totalTaskBonusReward).toNumber(),
      limit: new BigNumber(fullAmount).toNumber(),
      current: new BigNumber(bonus).toNumber(),
    };
  }

  //
  // 转盘配置
  //
  @action.bound
  setTurntableList(data: TurntableItem[]) {
    this.turntableList = data;
  }

  @action.bound
  setTurntableCount(data: number) {
    this.turntableCount = data;
  }

  @action.bound
  async fetchTurntableList(id: string | number) {
    const result: TurntableCfg = await Axios.get(API.ACTIVITY_LOTTERY_WHEEL, {
      params: {
        activityId: id,
      },
    });
    if (result) {
      this.setTurntableList(result.prize);
      this.setTurntableCount(result.num);
    }
  }

  @action.bound
  setTurntableTaskProgress(data: TurntableTaskProgress) {
    this.turntableTaskProgress = data;
  }

  @action.bound
  async fetchturntableTaskProgress(id: string | number) {
    const result: TurntableTaskProgress = await Axios.get(API.ACTIVITY_LOTTERY_WHEEL_TASK_PROGRESS, {
      params: {
        activityId: id,
      },
    });
    if (result) {
      this.setTurntableTaskProgress(result);
    }
  }

  @action.bound
  setInitialRewardInfo(data: InitialRewardInfo) {
    this.initialRewardInfo = data;
  }

  // 获取活动初始化奖励
  @action.bound
  async fetchInitialRewardInfo(id: string | number) {
    const result: InitialRewardInfo = await Axios.get(API.ACTIVITY_INITIAL_REWARD_INFO, {
      params: {
        activityId: id,
      },
    });
    if (result) {
      this.setInitialRewardInfo(result);
    }
  }

  // 点击领取初始化奖励
  @action.bound
  async fetchReceiveInitialRewardInfo(activityId: string | number): Promise<any> {
    const result = await Axios.post(API.ACTIVITY_RECEIVE_INITIAL_REWARD, {
      activityId,
    });

    if (result) {
      return Promise.resolve(result);
    }
    return Promise.reject();
  }

  @action.bound
  async fetchCountryInfo(type: 'All' | 'IP_REG' | 'JUMIO_KYC' | 'KYC_AUTH' | 'MOBILE' | 'REG' | 'XT_KYC') {
    this.countryInfo = await Axios.get(API.SIGNUP_COUNTRY_LIST, {
      params: {
        userBehaviorEnum: type,
      },
      baseURL: getUserCenterOrigin(),
    });
  }

  @action.bound
  async fetchCountryIpCheckInfo() {
    this.countryIpCheckInfo = await Axios.get(API.SIGNUP_COUNTRY_IP_CHECK, {
      baseURL: getUserCenterOrigin(),
    });
  }

  @action
  sendMsgCode(params: {
    codeType: string;
    countryCode: string;
    msgType: number;
    puzzleValidateString: string;
    receiveAddress: string;
    regChannel: string;
  }) {
    return Axios.post(`${API.SIGNUP_SENDING_MSG}?codeType=${params.codeType}`, params, {
      baseURL: getUserCenterOrigin(),
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  @action
  fetchPublicKeyInfo() {
    return Axios.post(
      API.SIGNUP_PASSWD_PUBLICKEY,
      {},
      {
        baseURL: getUserCenterOrigin(),
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );
  }

  @action
  mobileRegister(params: {
    activityCode: string;
    countryCode: string;
    dynamicCode: string;
    loginPwd: string;
    passwdId: string;
    recommendCode: string;
    regChannel: string;
    userName: string;
    xtCountryId: number;
  }) {
    const { activityCode, regChannel, ...restParams } = params;

    return Axios.post(
      `${API.SIGNUP_REG}?label=${activityCode}&ref=${restParams.recommendCode}&channelId=${regChannel}`,
      restParams,
      {
        baseURL: getUserCenterOrigin(),
        headers: {
          device: 'web',
          'api-version': 4,
          'client-code': getClientCode(),
          'Content-Type': 'application/json',
        },
      },
    );
  }

  @action
  setSafePassword(params: { mfaId: string; passwdId: string; safePwd: string }) {
    return Axios.post(API.SIGNUP_SET_SAFE_PWD, params, {
      baseURL: getUserCenterOrigin(),
      headers: {
        device: 'web',
        'api-version': 4,
        'client-code': getClientCode(),
        'Content-Type': 'application/json',
      },
    });
  }
}
